import { ApiError } from '../../../services/api';

/**
 * Rattache chaque erreur de validation au champ concerné.
 *
 * Le backend renvoie les erreurs Zod sous forme de tableau `issues[]` avec un
 * `path`. Les afficher groupées dans un bandeau oblige l'utilisateur à chercher
 * lui-même quel champ corriger : on les redistribue donc champ par champ.
 */
export type ErreursChamps = Record<string, string>;

interface ZodIssue {
  path?: (string | number)[];
  message?: string;
  code?: string;
}

/**
 * Messages Zod qui ne s'adressent PAS à l'utilisateur.
 *
 * « Unrecognized keys: … » signale un défaut du formulaire, qui a envoyé un
 * champ que le serveur ne connaît pas. Le montrer tel quel demande à
 * l'utilisateur de corriger quelque chose qu'il n'a pas fait et ne peut pas
 * réparer — en anglais, de surcroît.
 */
function estMessageTechnique(issue: ZodIssue): boolean {
  return (
    issue.code === 'unrecognized_keys' ||
    /^unrecognized keys/i.test(issue.message ?? '')
  );
}

export function erreursParChamp(err: unknown): ErreursChamps {
  if (!(err instanceof ApiError)) return {};
  const body = err.body as { issues?: ZodIssue[] } | null;
  if (!body?.issues?.length) return {};

  const map: ErreursChamps = {};
  for (const issue of body.issues) {
    if (estMessageTechnique(issue)) continue;
    const champ = issue.path?.[0];
    if (typeof champ === 'string' && issue.message && !map[champ]) {
      map[champ] = issue.message;
    }
  }
  return map;
}

/**
 * Message de repli quand aucune erreur n'a pu être rattachée à un champ visible.
 *
 * Sans lui, une erreur portant sur un champ absent de l'écran ne s'affiche
 * NULLE PART : l'utilisateur clique sur « Enregistrer » et rien ne se passe.
 * C'est arrivé sur le rattachement client, et c'est le pire cas possible — un
 * refus silencieux ressemble à une application cassée.
 */
export function messageSiAucunChampAffiche(
  err: unknown,
  champsAffiches: readonly string[],
): string | null {
  const parChamp = erreursParChamp(err);
  const cles = Object.keys(parChamp);
  if (cles.length === 0) return null;
  if (cles.some((c) => champsAffiches.includes(c))) return null;
  return cles.map((c) => parChamp[c]).join(' ');
}

/**
 * Le refus d'une PIÈCE JOINTE, qui ne s'affiche sous aucun champ.
 *
 * Un fichier n'a pas d'emplacement d'erreur à l'écran : on le choisit, il part.
 * Or le refus du serveur porte un chemin de champ (`justificatif.contenu_base64`,
 * `document.mime`…), et `erreurGlobale` ne remonte QUE les erreurs sans chemin.
 * Résultat sans cette fonction : un salarié dont la pièce est refusée lit
 * « L'envoi a échoué. », sans jamais savoir POURQUOI — le refus silencieux de
 * `messageSiAucunChampAffiche`, exactement le cas pour lequel ce fichier a été
 * écrit.
 *
 * Ce qui est refusé, c'est le TYPE et jamais le poids : seuls les fichiers qui
 * s'EXÉCUTENT à la réouverture (HTML, SVG, XHTML) sont écartés, parce qu'une
 * pièce est resservie avec son propre type dans un onglet. Les 10 Mo ne
 * concernent que les factures fournisseurs (règle de Valérie du 07/10/2026) :
 * aucune pièce RH n'est refusée sur sa taille, donc le message à faire remonter
 * ici dit un type, pas un poids.
 *
 * On ne regarde que les trois champs d'une pièce, pour ne pas faire remonter
 * ici une erreur qui s'affiche déjà, elle, sous son propre champ.
 */
const CHAMPS_DE_PIECE = ['contenu_base64', 'mime', 'filename'];

export function messageRefusPiece(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const body = err.body as { issues?: ZodIssue[] } | null;
  if (!body?.issues?.length) return null;
  const messages = body.issues
    .filter((i) => !estMessageTechnique(i))
    .filter((i) => (i.path ?? []).some((p) => typeof p === 'string' && CHAMPS_DE_PIECE.includes(p)))
    .map((i) => i.message)
    .filter(Boolean);
  return messages.length ? [...new Set(messages)].join(' ') : null;
}

/**
 * Reste-t-il une erreur qui ne vise aucun champ ? (conflit d'e-mail, doublon…)
 * Celles-là méritent un message global — les autres non.
 */
export function erreurGlobale(err: unknown): string | null {
  if (!(err instanceof ApiError)) {
    return err instanceof Error ? err.message : 'Une erreur est survenue.';
  }
  const body = err.body as { issues?: ZodIssue[]; error?: string } | null;
  if (body?.issues?.length) {
    if (body.issues.some(estMessageTechnique)) {
      return 'L’enregistrement a été refusé pour une raison technique. Vos saisies sont conservées : signalez-le, le problème vient du formulaire et non de vos données.';
    }
    // Les autres sont déjà affichées sous leur champ ; ne remontent ici que
    // celles qui ne visent aucun champ précis.
    const sansChamp = body.issues.filter((i) => !i.path?.length);
    return sansChamp.length ? (sansChamp[0]?.message ?? null) : null;
  }
  return body?.error ?? err.message;
}
